import React, { useState, useEffect, useCallback, useRef } from "react";
import { useSettings } from "../../contexts/SettingsContext";
import { useQuickIsland } from "../../contexts/QuickIslandContext";
import { usePreferences } from "../../hooks/usePreferences";
import { MapStyleId } from "../../types";
import { getMapStyleInfo } from "../../config/mapStyles";
import { getSimulationService } from "../../services/simulationService";
import { getRouteService } from "../../services/routeService";
import { QuickIslandSearchDetail } from "./QuickIslandSearchDetail";
import { getSearchService } from "../../services/searchService";
import { getOverpassWorkerService } from "../../services/overpassWorkerService";
import { useActivity } from "../../hooks/useActivity";
import { MapStyleButton, MapStyleOption } from "./MapStyleButton";
import { MapStylesDetailView } from "../QuickIsland/MapStylesDetailView";
import { SimulationDetailView } from "../QuickIsland/SimulationDetailView";
import { RouteDetailView } from "../QuickIsland/RouteDetailView";
import { CameraControlView } from "../QuickIsland/CameraControlView";
import debugx from "debugx";
import {
  wakeLockService,
  WakeLockStatus,
} from "../../services/wakeLockService";

const debug = debugx("bikedash:quick-island");

type DetailViewType =
  | "map-styles"
  | "simulation"
  | "search"
  | "route"
  | "camera-controls"
  | null;

// Removed internal MapStyleButton - now using unified component from ./MapStyleButton

export const PersistentQuickIsland: React.FC = () => {
  const [currentTime, setCurrentTime] = useState(new Date());
  const { expandedView, openIsland, closeIsland, searchBusy, isSearching } =
    useQuickIsland();
  const [selectedMapStyle, setSelectedMapStyle] = useState<MapStyleId | null>(
    null,
  );
  const [simulationProgress, setSimulationProgress] = useState(0);
  const [isSimulationActive, setIsSimulationActive] = useState(false);
  const [isSimulationPaused, setIsSimulationPaused] = useState(false);
  const [simulationSpeed, setSimulationSpeed] = useState(0);
  const [simulationDistance, setSimulationDistance] = useState(0);
  const [simulationTotalDistance, setSimulationTotalDistance] = useState(0);
  const [simulationTime, setSimulationTime] = useState(0);
  const [simulationTotalTime, setSimulationTotalTime] = useState(0);
  const [isRouteActive, setIsRouteActive] = useState(false);
  const [routeName, setRouteName] = useState<string | null>(null);
  const [routeDistance, setRouteDistance] = useState(0);
  const [isRouteSaved, setIsRouteSaved] = useState(false);
  const [isRenamingRoute, setIsRenamingRoute] = useState(false);
  const [renameValue, setRenameValue] = useState("");
  const longPressTimer = useRef<NodeJS.Timeout | null>(null);
  const { openSettings } = useSettings();
  const { mapPreferences, updateMapPreferences, userPreferences } =
    usePreferences();
  const simulationService = getSimulationService();
  const routeService = getRouteService();
  const searchService = getSearchService();
  const workerService = getOverpassWorkerService();
  const { currentPosition } = useActivity();

  // Search state
  const [activeLocalSearch, setActiveLocalSearch] = useState(false);
  const [currentMapCenter, setCurrentMapCenter] = useState<{
    lat: number;
    lon: number;
  } | null>(null);
  const [cacheGridState, setCacheGridState] = useState<
    ("cached" | "failed" | "fetching" | "none")[]
  >(new Array(9).fill("none"));
  const lastGridCenterRef = useRef<{ lat: number; lon: number } | null>(null);
  const isUpdatingCacheRef = useRef(false);
  const [wakeLockStatus, setWakeLockStatus] =
    useState<WakeLockStatus>("disabled");
  const [batteryLevel, setBatteryLevel] = useState<number | null>(null);
  const [isCharging, setIsCharging] = useState<boolean>(false);
  const [showCameraConsole, setShowCameraConsole] = useState(false);
  const [cameraLogs, setCameraLogs] = useState<string[]>([]);
  const cameraLogsEndRef = useRef<HTMLDivElement>(null);

  // Load initial config from localStorage SYNCHRONOUSLY (before any effects run)
  const getInitialUiConfig = () => {
    try {
      const stored = localStorage.getItem("cacheGridUiConfig");
      if (stored) {
        const config = JSON.parse(stored);
        return { cacheGridSize: 3, alwaysShowCacheGrid: false, ...config };
      }
    } catch (error) {
      debug("Failed to parse stored UI config:", error);
    }
    return { cacheGridSize: 3, alwaysShowCacheGrid: false };
  };

  const [uiConfig, setUiConfig] = useState<{
    cacheGridSize: 3 | 5 | 7 | 9;
    alwaysShowCacheGrid: boolean;
  }>(getInitialUiConfig());

  // Memoize blue dot position to avoid recalculating on every render
  const blueDotPosition = React.useMemo(() => {
    if (!currentMapCenter) return null;

    const GRID_SIZE = 0.2;
    const centerGridLat =
      Math.round(currentMapCenter.lat / GRID_SIZE) * GRID_SIZE;
    const centerGridLon =
      Math.round(currentMapCenter.lon / GRID_SIZE) * GRID_SIZE;

    const latOffset = currentMapCenter.lat - centerGridLat;
    const lonOffset = currentMapCenter.lon - centerGridLon;

    // Grid is always 24x24px total
    const totalSize = 24;
    const center = totalSize / 2;

    // Calculate cell size (same formula as in render)
    const padding = 2;
    const gap = 0.5;
    const availableSize =
      totalSize - padding * 2 - gap * (uiConfig.cacheGridSize - 1);
    const cellSize = availableSize / uiConfig.cacheGridSize;

    // One cell = 0.2 degrees, so pixels per 0.1 degree = cellSize / 2
    const pixelsPerTenth = cellSize / 2;

    const pixelX = center + (lonOffset / 0.1) * pixelsPerTenth;
    const pixelY = center - (latOffset / 0.1) * pixelsPerTenth;

    return { pixelX, pixelY };
  }, [currentMapCenter, uiConfig.cacheGridSize]);

  // Load UI config
  useEffect(() => {
    const loadUiConfig = () => {
      const stored = localStorage.getItem("cacheGridUiConfig");
      if (stored) {
        try {
          const config = JSON.parse(stored);
          setUiConfig({ ...uiConfig, ...config });
        } catch (error) {
          debug("Failed to load UI config:", error);
        }
      }
    };

    loadUiConfig();

    // Listen for config changes
    const handleConfigChange = (e: CustomEvent) => {
      setUiConfig(e.detail);
      // Force cache grid refresh by clearing last grid center
      lastGridCenterRef.current = null;
    };

    window.addEventListener(
      "cacheGridUiConfigChanged",
      handleConfigChange as any,
    );

    return () => {
      window.removeEventListener(
        "cacheGridUiConfigChanged",
        handleConfigChange as any,
      );
    };
  }, []);

  // Subscribe to wake lock status changes
  useEffect(() => {
    const unsubscribe = wakeLockService.subscribe((status) => {
      setWakeLockStatus(status);
    });
    return unsubscribe;
  }, []);

  // Monitor battery status
  useEffect(() => {
    const updateBatteryStatus = (battery: any) => {
      setBatteryLevel(battery.level);
      setIsCharging(battery.charging);
    };

    // Battery Status API
    if ("getBattery" in navigator) {
      (navigator as any).getBattery().then((battery: any) => {
        updateBatteryStatus(battery);

        battery.addEventListener("levelchange", () =>
          updateBatteryStatus(battery),
        );
        battery.addEventListener("chargingchange", () =>
          updateBatteryStatus(battery),
        );
      });
    }
  }, []);

  // Auto-scroll camera console when new logs arrive
  useEffect(() => {
    if (showCameraConsole && cameraLogsEndRef.current) {
      cameraLogsEndRef.current.scrollIntoView({ behavior: "smooth" });
    }
  }, [cameraLogs, showCameraConsole]);

  // Update clock every minute
  useEffect(() => {
    // Update immediately to next minute
    const now = new Date();
    const secondsUntilNextMinute = 60 - now.getSeconds();

    // Set initial timeout to sync with minute boundary
    const initialTimeout = setTimeout(() => {
      setCurrentTime(new Date());

      // Then update every minute
      const timer = setInterval(() => {
        setCurrentTime(new Date());
      }, 60000);

      // Store timer for cleanup
      (window as any).__clockTimer = timer;
    }, secondsUntilNextMinute * 1000);

    return () => {
      clearTimeout(initialTimeout);
      if ((window as any).__clockTimer) {
        clearInterval((window as any).__clockTimer);
      }
    };
  }, []);

  // Format time - split into parts for clickable halves
  const formatTimeParts = (date: Date) => {
    let hours = date.getHours();
    const minutes = date.getMinutes();

    // Convert to 12-hour format
    if (hours === 0) {
      hours = 12;
    } else if (hours > 12) {
      hours = hours - 12;
    }

    return {
      hours: hours.toString(),
      minutes: minutes.toString().padStart(2, "0"),
    };
  };

  // Monitor search state for active local searches
  useEffect(() => {
    const updateSearchState = () => {
      const searchState = searchService.getState();
      const hasLocalSearch =
        searchState.query && searchState.distance === "local";
      setActiveLocalSearch(!!hasLocalSearch);
    };

    // Update immediately
    updateSearchState();

    // Listen for search state changes
    const unsubscribe = searchService.subscribe(updateSearchState);
    return () => unsubscribe();
  }, [searchService]);

  // Update current map center frequently for real-time blue dot position
  useEffect(() => {
    if (
      (!activeLocalSearch && !uiConfig.alwaysShowCacheGrid) ||
      expandedView === "search"
    ) {
      return;
    }

    const updateMapCenter = () => {
      const mapService = window.__bikedash_maps?.["main"];
      const center = mapService?.getCenter();
      if (center) {
        setCurrentMapCenter({ lat: center.lat, lon: center.lon });
      }
    };

    // Update immediately
    updateMapCenter();

    // If map isn't ready yet, keep checking every 100ms until it is
    // Once we have a center, update every 100ms for real-time position tracking
    const interval = setInterval(updateMapCenter, 100);

    return () => clearInterval(interval);
  }, [activeLocalSearch, expandedView, uiConfig.alwaysShowCacheGrid]);

  // Update cache grid state when crossing grid boundaries
  useEffect(() => {
    if (
      (!activeLocalSearch && !uiConfig.alwaysShowCacheGrid) ||
      expandedView === "search" ||
      !currentMapCenter
    ) {
      return;
    }

    const GRID_SIZE = 0.2;
    const centerGridLat =
      Math.round(currentMapCenter.lat / GRID_SIZE) * GRID_SIZE;
    const centerGridLon =
      Math.round(currentMapCenter.lon / GRID_SIZE) * GRID_SIZE;

    // Skip if we're still in the same grid tile
    if (
      lastGridCenterRef.current &&
      lastGridCenterRef.current.lat === centerGridLat &&
      lastGridCenterRef.current.lon === centerGridLon
    ) {
      return;
    }

    // Skip if already updating
    if (isUpdatingCacheRef.current) {
      return;
    }

    lastGridCenterRef.current = { lat: centerGridLat, lon: centerGridLon };
    isUpdatingCacheRef.current = true;

    // Generate offsets based on grid size (3x3, 5x5, or 7x7)
    const halfSize = Math.floor(uiConfig.cacheGridSize / 2);
    const offsets: [number, number][] = [];
    for (let latOffset = halfSize; latOffset >= -halfSize; latOffset--) {
      for (let lonOffset = -halfSize; lonOffset <= halfSize; lonOffset++) {
        offsets.push([latOffset, lonOffset]);
      }
    }

    const tiles = offsets.map(([latOffset, lonOffset]) => ({
      gridLat: centerGridLat + latOffset * GRID_SIZE,
      gridLon: centerGridLon + lonOffset * GRID_SIZE,
    }));

    workerService
      .checkGridStatusBatch(tiles)
      .then((statuses) => {
        setCacheGridState(statuses);
      })
      .finally(() => {
        isUpdatingCacheRef.current = false;
      });
  }, [
    activeLocalSearch,
    currentMapCenter,
    expandedView,
    workerService,
    uiConfig.cacheGridSize,
    uiConfig.alwaysShowCacheGrid,
  ]);

  // Listen for cache updates from worker
  useEffect(() => {
    if (
      (!activeLocalSearch && !uiConfig.alwaysShowCacheGrid) ||
      expandedView === "search" ||
      !currentMapCenter
    ) {
      return;
    }

    const handleCacheUpdate = () => {
      // Force re-check by clearing the last grid center
      lastGridCenterRef.current = null;
      // Trigger update by setting a new object reference
      setCurrentMapCenter({ ...currentMapCenter });
    };

    window.addEventListener("cacheUpdated", handleCacheUpdate);

    return () => {
      window.removeEventListener("cacheUpdated", handleCacheUpdate);
    };
  }, [
    activeLocalSearch,
    currentMapCenter,
    expandedView,
    uiConfig.alwaysShowCacheGrid,
  ]);

  // Update route status and restore route on map
  useEffect(() => {
    const unsubscribe = routeService.subscribe((state) => {
      setIsRouteActive(state.isActive);
      setRouteName(state.routeName);
      setRouteDistance(state.totalDistance / 1000); // Convert to km
      setIsRouteSaved(state.isSaved);

      // Restore route geometry on map if active
      if (state.isActive && state.geometry) {
        const mapService = window.__bikedash_maps?.["main"];
        if (mapService) {
          debug("Restoring route on map: %s", state.routeName);
          const overpassWorker = getOverpassWorkerService();
          // Don't clear previous or re-fit - route is already displayed from activation or being restored on load
          mapService.displaySelectedFeature(
            state.geometry,
            overpassWorker,
            false,
            false,
            true,
          );
        }
      }
    });

    return unsubscribe;
  }, [routeService]);

  // Display saved routes as markers on the map
  useEffect(() => {
    const updateSavedRoutes = () => {
      const mapService = window.__bikedash_maps?.["main"];
      if (mapService) {
        const savedRoutes = routeService.getSavedRoutes();
        debug(
          "Updating saved route markers on map: %d routes",
          savedRoutes.length,
        );
        mapService.displaySavedRoutes(savedRoutes);
      }
    };

    // Initial display
    updateSavedRoutes();

    // Subscribe to changes
    const unsubscribe = routeService.subscribeSavedRoutes(updateSavedRoutes);

    return unsubscribe;
  }, [routeService]);

  // Update simulation status
  useEffect(() => {
    const updateSimulationStatus = () => {
      const simState = simulationService.getState();
      setIsSimulationActive(simState.isActive);
      setIsSimulationPaused(simState.isPaused);

      if (simState.isActive) {
        const progress = simulationService.getProgress();
        setSimulationProgress(progress.overallProgress);

        // Get route and simulation options like SimulationIsland does
        const currentRoute = simulationService.getCurrentRoute();
        const simOptions = simState.simulationOptions;

        if (currentRoute) {
          // Calculate speed from route data (m/s to km/h)
          const baseSpeed =
            (currentRoute.totalDistance || 0) /
            (currentRoute.estimatedTime || 1);
          const speedMultiplier = simOptions?.speedMultiplier || 1;
          const speedKmh = baseSpeed * speedMultiplier * 3.6;
          setSimulationSpeed(speedKmh);

          // Distance: current and total in km
          const totalDistKm = (currentRoute.totalDistance || 0) / 1000;
          const currentDistKm = progress.overallProgress * totalDistKm;
          setSimulationDistance(currentDistKm);
          setSimulationTotalDistance(totalDistKm);

          // Time: current and total adjusted for speed multiplier
          const totalTimeSeconds =
            (currentRoute.estimatedTime || 0) / speedMultiplier;
          const elapsedTimeSeconds =
            progress.overallProgress * totalTimeSeconds;
          setSimulationTime(elapsedTimeSeconds);
          setSimulationTotalTime(totalTimeSeconds);
        }
      } else {
        setSimulationProgress(0);
        setSimulationSpeed(0);
        setSimulationDistance(0);
        setSimulationTotalDistance(0);
        setSimulationTime(0);
        setSimulationTotalTime(0);
      }
    };

    updateSimulationStatus();
    const interval = setInterval(updateSimulationStatus, 200);
    return () => clearInterval(interval);
  }, [simulationService]);

  // Handle settings button
  const handleSettingsClick = useCallback(() => {
    openSettings("map");
    closeIsland();
  }, [openSettings]);

  // Search icon handler
  const handleSearchClick = useCallback(() => {
    const willOpen = expandedView !== "search";
    openIsland(expandedView === "search" ? null : "search");
    // Trigger tile preload when search opens
    if (willOpen) {
      searchService.triggerTilePreload();
    }
  }, [expandedView, searchService]);

  const handleSimulationClick = useCallback(() => {
    openIsland(expandedView === "simulation" ? null : "simulation");
  }, [expandedView]);

  const handleRouteClick = useCallback(() => {
    openIsland(expandedView === "route" ? null : "route");
  }, [expandedView]);

  const handleRouteStop = useCallback(() => {
    routeService.stopRoute();
    // Clear the selected features on the map
    const mapService = window.__bikedash_maps?.["main"];
    if (mapService) {
      mapService.clearSelectedFeature();
    }
    closeIsland();
  }, [routeService]);

  const handleRouteZoom = useCallback(() => {
    const routeState = routeService.getState();
    if (routeState.isActive && routeState.geometry) {
      const mapService = window.__bikedash_maps?.["main"];
      const overpassWorker = getOverpassWorkerService();
      if (mapService) {
        // Set mode to free before zooming
        mapService.setMode("free");
        mapService.setRotation(0);
        // Re-display the feature which will zoom to fit
        mapService.displaySelectedFeature(routeState.geometry, overpassWorker);
      }
    }
  }, [routeService]);

  const handleToggleSaveRoute = useCallback(async () => {
    if (isRouteSaved) {
      await routeService.unsaveCurrentRoute();
    } else {
      await routeService.saveCurrentRoute();
    }
  }, [isRouteSaved, routeService]);

  const handleRenameRoute = useCallback(async () => {
    if (renameValue.trim()) {
      await routeService.renameCurrentRoute(renameValue.trim());
      setIsRenamingRoute(false);
      setRenameValue("");
    }
  }, [renameValue, routeService]);

  // Get available map styles
  const getMapStyles = useCallback((): MapStyleOption[] => {
    const freeStyles: MapStyleId[] = [
      "osm:standard",
      "osm:humanitarian",
      "osm:topographic",
      "cartodb:light",
      "cartodb:dark",
      "cartodb:voyager",
      "esri:gray",
      "esri:satellite",
    ];

    const thunderforestStyles: MapStyleId[] = [
      "thunderforest:cycle",
      "thunderforest:landscape",
      "thunderforest:outdoors",
      "thunderforest:transport",
      "thunderforest:transport-dark",
      "thunderforest:spinal-map",
      "thunderforest:pioneer",
      "thunderforest:mobile-atlas",
      "thunderforest:neighbourhood",
    ];

    // Check for API key
    const cachedAuth = localStorage.getItem("bikedash_cached_auth");
    let hasApiKey = false;
    if (cachedAuth) {
      try {
        const cached = JSON.parse(cachedAuth);
        hasApiKey = !!cached.thunderforestApiKey;
      } catch (e) {
        // Ignore
      }
    }
    if (!hasApiKey) {
      hasApiKey = !!userPreferences.apiKeys?.thunderforest;
    }

    const allStyles = [
      ...freeStyles.map((id) => ({
        id,
        name: getMapStyleInfo(id)?.name || id.split(":")[1],
        icon: getMapStyleInfo(id)?.icon || "🗺️",
        available: true,
      })),
      ...thunderforestStyles.map((id) => ({
        id,
        name: getMapStyleInfo(id)?.name || id.split(":")[1],
        icon: getMapStyleInfo(id)?.icon || "🗺️",
        available: hasApiKey,
      })),
    ];

    return allStyles;
  }, [userPreferences.apiKeys]);

  const handleSelectMapStyle = useCallback(
    (styleId: MapStyleId) => {
      updateMapPreferences({ defaultStyleId: styleId });
      setSelectedMapStyle(styleId);
      // Keep the quick island open after selection
      setTimeout(() => {
        setSelectedMapStyle(null);
      }, 300);
      debug("Selected map style:", styleId);
    },
    [updateMapPreferences],
  );

  const handleToggleStar = useCallback(
    (styleId: MapStyleId) => {
      const currentStarred = mapPreferences.starredStyleIds;
      const isStarred = currentStarred.includes(styleId);
      updateMapPreferences({
        starredStyleIds: isStarred
          ? currentStarred.filter((id) => id !== styleId)
          : [...currentStarred, styleId],
      });
      debug(isStarred ? "Unstarred" : "Starred", styleId);
    },
    [mapPreferences.starredStyleIds, updateMapPreferences],
  );

  // No auto-close - user must manually close or click outside

  // Simulation control handlers
  const handleSimulationPause = useCallback(() => {
    simulationService.pause();
  }, [simulationService]);

  const handleSimulationStop = useCallback(() => {
    simulationService.stop();
    closeIsland();
  }, [simulationService]);

  // Handle swipe down to open island
  const swipeStartY = useRef<number>(0);
  const swipeStartX = useRef<number>(0);
  const swipeStartTarget = useRef<string | null>(null);
  const islandCompactRef = useRef<HTMLDivElement>(null);

  const handleTouchStart = (e: React.TouchEvent) => {
    if (expandedView) return; // Don't handle swipes when already expanded

    swipeStartY.current = e.touches[0].clientY;
    swipeStartX.current = e.touches[0].clientX;

    // Detect which button/area was touched
    const target = e.target as HTMLElement;
    if (target.closest('[data-island-button="map-styles"]')) {
      swipeStartTarget.current = "map-styles";
    } else if (target.closest('[data-island-button="simulation"]')) {
      swipeStartTarget.current = "simulation";
    } else if (target.closest('[data-island-button="route"]')) {
      swipeStartTarget.current = "route";
    } else if (target.closest('[data-island-button="search"]')) {
      swipeStartTarget.current = "search";
    } else {
      // Auto-detect based on X position (left/right of center)
      const island = islandCompactRef.current;
      if (island) {
        const rect = island.getBoundingClientRect();
        const centerX = rect.left + rect.width / 2;
        if (swipeStartX.current < centerX) {
          swipeStartTarget.current = "search"; // Left half
        } else {
          swipeStartTarget.current = isRouteActive ? "route" : "search"; // Right half
        }
      } else {
        swipeStartTarget.current = "search"; // Fallback
      }
    }
  };

  const handleTouchMove = (e: React.TouchEvent) => {
    if (expandedView) return; // Don't handle swipes when already expanded

    const currentY = e.touches[0].clientY;
    const swipeDistance = currentY - swipeStartY.current;
    const swipeThreshold = 30; // pixels

    // Don't call preventDefault here - it breaks React's event system!
    // The native listener will handle preventing pull-to-refresh

    // If swiped down more than threshold, open the appropriate view immediately
    if (swipeDistance > swipeThreshold && swipeStartTarget.current) {
      openIsland(swipeStartTarget.current as DetailViewType);
      swipeStartTarget.current = null; // Prevent opening again
    }
  };

  const handleTouchEnd = (e: React.TouchEvent) => {
    if (expandedView) return; // Don't handle swipes when already expanded

    const swipeEndY = e.changedTouches[0].clientY;
    const swipeDistance = swipeEndY - swipeStartY.current;
    const swipeThreshold = 30; // pixels

    // If swiped down more than threshold, open the appropriate view
    if (swipeDistance > swipeThreshold && swipeStartTarget.current) {
      openIsland(swipeStartTarget.current as DetailViewType);
    }

    swipeStartTarget.current = null;
  };

  // Native event listener to prevent pull-to-refresh without breaking React
  useEffect(() => {
    const container = islandCompactRef.current;
    if (!container) return;

    let startY = 0;

    const handleNativeTouchStart = (e: TouchEvent) => {
      startY = e.touches[0].clientY;
    };

    const handleNativeTouchMove = (e: TouchEvent) => {
      const currentY = e.touches[0].clientY;
      const swipeDistance = currentY - startY;

      // Prevent pull-to-refresh when swiping down
      if (swipeDistance > 0) {
        e.preventDefault();
      }
    };

    // Use non-passive listeners so we can preventDefault
    container.addEventListener("touchstart", handleNativeTouchStart, {
      passive: false,
    });
    container.addEventListener("touchmove", handleNativeTouchMove, {
      passive: false,
    });

    return () => {
      container.removeEventListener("touchstart", handleNativeTouchStart);
      container.removeEventListener("touchmove", handleNativeTouchMove);
    };
  }, []);

  // Listen for map clicks to close island (unified for all views that allow map interaction)
  useEffect(() => {
    // Only add listener when island is expanded
    if (!expandedView) return;

    let startPixel: [number, number] | null = null;
    let isDragging = false;
    const MOVEMENT_THRESHOLD = 10; // pixels

    const handlePointerDown = (e: PointerEvent) => {
      startPixel = [e.clientX, e.clientY];
      isDragging = false;
    };

    const handlePointerMove = (e: PointerEvent) => {
      if (startPixel) {
        const dx = Math.abs(e.clientX - startPixel[0]);
        const dy = Math.abs(e.clientY - startPixel[1]);
        if (dx > MOVEMENT_THRESHOLD || dy > MOVEMENT_THRESHOLD) {
          isDragging = true;
        }
      }
    };

    const handlePointerUp = (e: PointerEvent) => {
      // Only close if it was a pure click (not a drag)
      if (!isDragging && startPixel) {
        const target = e.target as HTMLElement;
        // Don't close if clicking on the island itself or camera console
        const island = document.querySelector(".dynamic-island");
        const cameraConsole = document.querySelector(".camera-console");
        if (!island?.contains(target) && !cameraConsole?.contains(target)) {
          debug("Map clicked (not dragged) - closing island: %s", expandedView);
          closeIsland();
        }
      }

      startPixel = null;
      isDragging = false;
    };

    document.addEventListener("pointerdown", handlePointerDown, true);
    document.addEventListener("pointermove", handlePointerMove, true);
    document.addEventListener("pointerup", handlePointerUp, true);

    return () => {
      document.removeEventListener("pointerdown", handlePointerDown, true);
      document.removeEventListener("pointermove", handlePointerMove, true);
      document.removeEventListener("pointerup", handlePointerUp, true);
    };
  }, [expandedView, closeIsland]);

  return (
    <>
      {/* No overlay - all views allow map interaction and dismiss on click */}

      {/* Main persistent island bar */}
      <div
        className={`dynamic-island ${expandedView ? "expanded" : ""}`}
        onClick={expandedView ? undefined : () => {}}
      >
        {/* Compact View (always visible) */}
        <div
          ref={islandCompactRef}
          className="island-compact-container"
          onTouchStart={handleTouchStart}
          onTouchMove={handleTouchMove}
          onTouchEnd={handleTouchEnd}
          style={{
            opacity: expandedView ? 0 : 1,
            transition: "opacity 0.4s cubic-bezier(0.25, 0.46, 0.45, 0.94)",
            touchAction: "pan-x", // Allow horizontal scroll but prevent vertical pull-to-refresh
          }}
        >
          <div
            className="island-compact"
            style={{ justifyContent: "space-between", padding: "0 8px" }}
          >
            {/* Search icon or cache grid */}
            <button
              className="quick-island-icon-btn"
              onClick={handleSearchClick}
              title="Search"
              data-island-button="search"
            >
              <div className="quick-island-search-icon">
                {isSearching && (
                  <svg
                    width="24"
                    height="24"
                    viewBox="0 0 24 24"
                    className="quick-island-search-spinner"
                  >
                    <circle
                      cx="12"
                      cy="12"
                      r="10"
                      fill="none"
                      strokeWidth="2"
                      strokeDasharray="47.1 62.8"
                      strokeLinecap="round"
                    />
                  </svg>
                )}
                <span
                  style={{
                    position: "relative",
                    zIndex: 1,
                    fontSize: "14px",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                  }}
                >
                  {activeLocalSearch || uiConfig.alwaysShowCacheGrid
                    ? // Show cache grid for active local search OR if always-show is enabled
                      (() => {
                        // Calculate cell size to fit in 24x24px total area
                        // Formula: (cellSize * gridSize) + (gap * (gridSize - 1)) + (padding * 2) = 24
                        const totalSize = 24;
                        const padding = 2;
                        const gap = 0.5; // Use smaller gap for better fit
                        const availableSize =
                          totalSize -
                          padding * 2 -
                          gap * (uiConfig.cacheGridSize - 1);
                        const cellSize = availableSize / uiConfig.cacheGridSize;

                        return (
                          <div
                            className="quick-island-cache-grid"
                            style={{
                              gridTemplateColumns: `repeat(${uiConfig.cacheGridSize}, ${cellSize}px)`,
                              gridTemplateRows: `repeat(${uiConfig.cacheGridSize}, ${cellSize}px)`,
                            }}
                            title="Cache coverage for search area"
                          >
                            {cacheGridState.map((status, index) => (
                              <div
                                key={index}
                                className={`quick-island-cache-cell quick-island-cache-cell--${status}`}
                              />
                            ))}
                            {/* Blue dot for current map center position */}
                            {blueDotPosition && (
                              <div
                                className="quick-island-cache-dot"
                                style={{
                                  left: `${blueDotPosition.pixelX}px`,
                                  top: `${blueDotPosition.pixelY}px`,
                                }}
                              />
                            )}
                          </div>
                        );
                      })()
                    : "🔍"}
                </span>
              </div>
            </button>

            {/* Clock in center */}
            <div
              className="quick-island-clock"
              style={{ display: "flex", alignItems: "center" }}
            >
              <span onClick={handleSearchClick} style={{ cursor: "pointer" }}>
                {formatTimeParts(currentTime).hours}:
              </span>
              <span
                onClick={isRouteActive ? handleRouteClick : handleSearchClick}
                style={{
                  cursor: "pointer",
                  display: "flex",
                  alignItems: "center",
                }}
              >
                {formatTimeParts(currentTime).minutes}
                {/* Battery indicator */}
                {batteryLevel !== null && (
                  <svg
                    width="12"
                    height="22"
                    viewBox="0 0 12 22"
                    style={{
                      marginLeft: "6px",
                      display: "inline-block",
                      verticalAlign: "middle",
                    }}
                  >
                    {/* Battery outline */}
                    <rect
                      x="1"
                      y="2"
                      width="10"
                      height="18"
                      rx="1.5"
                      fill="none"
                      stroke={
                        batteryLevel <= 0.2
                          ? "#F44336"
                          : batteryLevel < 0.5
                            ? "#FFC107"
                            : "rgba(255,255,255,0.6)"
                      }
                      strokeWidth="1"
                    />
                    {/* Battery terminal */}
                    <rect
                      x="4"
                      y="0"
                      width="4"
                      height="2"
                      rx="0.5"
                      fill="rgba(255,255,255,0.6)"
                    />
                    {/* Black background for low battery (easier to see yellow/red) */}
                    {batteryLevel < 0.5 && (
                      <rect
                        x="2"
                        y="3"
                        width="8"
                        height="17"
                        rx="0.5"
                        fill="rgba(0,0,0,0.6)"
                      />
                    )}
                    {/* Battery fill */}
                    <rect
                      x="2"
                      y={2 + (1 - batteryLevel) * 17}
                      width="8"
                      height={batteryLevel * 17}
                      rx="0.5"
                      fill={
                        isCharging
                          ? "#4CAF50"
                          : batteryLevel > 0.5
                            ? "#4CAF50"
                            : batteryLevel > 0.2
                              ? "#FFC107"
                              : "#F44336"
                      }
                    />
                    {/* Charging bolt */}
                    {isCharging && (
                      <path
                        d="M 7 6 L 5 11 L 6.5 11 L 5.5 16 L 8 10.5 L 6.5 10.5 Z"
                        fill="white"
                        opacity="0.9"
                      />
                    )}
                  </svg>
                )}
              </span>
            </div>

            {/* Simulation icon with progress ring (only when active) */}
            {isSimulationActive && (
              <button
                className="quick-island-icon-btn"
                onClick={handleSimulationClick}
                data-island-button="simulation"
              >
                <svg
                  width="24"
                  height="24"
                  style={{
                    position: "absolute",
                    top: "50%",
                    left: "50%",
                    transform: "translate(-50%, -50%) rotate(-90deg)",
                  }}
                >
                  <circle
                    cx="12"
                    cy="12"
                    r="10"
                    fill="none"
                    stroke="rgba(255,255,255,0.1)"
                    strokeWidth="2"
                  />
                  <circle
                    cx="12"
                    cy="12"
                    r="10"
                    fill="none"
                    stroke="#ff9500"
                    strokeWidth="2"
                    strokeDasharray={`${simulationProgress * 62.8} 62.8`}
                    strokeLinecap="round"
                  />
                </svg>
                <span style={{ position: "relative", zIndex: 1 }}>📍</span>
              </button>
            )}

            {/* Route icon when active */}
            {isRouteActive && (
              <button
                className="quick-island-route-icon"
                onClick={handleRouteClick}
                data-island-button="route"
              >
                <svg width="22" height="18" viewBox="0 0 24 20" fill="none">
                  <path
                    d="M 2,14 C 6,14 6,6 10,6 C 14,6 14,14 18,14 C 22,14 22,6 22,6"
                    strokeWidth="2.5"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    fill="none"
                  />
                </svg>
              </button>
            )}

            {/* Placeholder when both simulation and route are inactive */}
            {!isSimulationActive && !isRouteActive && (
              <div className="quick-island-placeholder" />
            )}
          </div>
        </div>

        {/* Expanded View */}
        <div
          className="island-expanded-container"
          style={{
            opacity: expandedView ? 1 : 0,
            transition: "opacity 0.4s cubic-bezier(0.25, 0.46, 0.45, 0.94)",
            pointerEvents: expandedView ? "auto" : "none",
          }}
          onClick={(e) => e.stopPropagation()}
        >
          <div
            className="island-expanded-content"
            style={{
              transform: `scale(${expandedView ? 1 : 0.47})`,
              transition: "transform 0.4s cubic-bezier(0.25, 0.46, 0.45, 0.94)",
            }}
          >
            {/* Wake lock indicator inside expanded island */}
            {wakeLockStatus === "needs-gesture" && expandedView && (
              <div
                onClick={() => wakeLockService.handleUserGesture()}
                className="wake-lock-indicator wake-lock-indicator--centered"
                title="Tap to keep screen awake"
              >
                <span className="wake-lock-indicator-icon">☀️</span>
                <span>TAP TO WAKE</span>
              </div>
            )}

            {/* Map Styles View */}
            {expandedView === "map-styles" && (
              <MapStylesDetailView
                mapPreferences={mapPreferences}
                mapStyles={getMapStyles()}
                onSelectMapStyle={handleSelectMapStyle}
                onToggleStar={handleToggleStar}
                onUpdateOverlay={(opacity) =>
                  updateMapPreferences({ overlayOpacity: opacity })
                }
                onSettingsClick={handleSettingsClick}
                onClose={() => closeIsland()}
              />
            )}

            {/* Simulation View */}
            {expandedView === "simulation" && (
              <SimulationDetailView
                isActive={isSimulationActive}
                isPaused={isSimulationPaused}
                progress={simulationProgress}
                speed={simulationSpeed}
                distance={simulationDistance}
                totalDistance={simulationTotalDistance}
                time={simulationTime}
                totalTime={simulationTotalTime}
                onPause={handleSimulationPause}
                onStop={handleSimulationStop}
                onOpenSettings={() => openSettings("debug")}
                onClose={() => closeIsland()}
              />
            )}

            {/* Route View */}
            {expandedView === "route" && (
              <RouteDetailView
                routeName={routeName}
                routeDistance={routeDistance}
                isSaved={isRouteSaved}
                isRenaming={isRenamingRoute}
                renameValue={renameValue}
                onToggleSave={handleToggleSaveRoute}
                onRename={handleRenameRoute}
                onRenameChange={setRenameValue}
                onSetRenaming={setIsRenamingRoute}
                onZoom={handleRouteZoom}
                onStop={handleRouteStop}
                onClose={() => closeIsland()}
              />
            )}

            {/* Search View */}
            {expandedView === "search" && <QuickIslandSearchDetail />}

            {/* Camera Controls View */}
            {expandedView === "camera-controls" && (
              <CameraControlView
                onClose={() => closeIsland()}
                onShowConsoleChange={setShowCameraConsole}
                onLogsChange={setCameraLogs}
              />
            )}
          </div>
        </div>
      </div>

      {/* Fixed wake lock indicator - centered below quick island (only when collapsed) */}
      {wakeLockStatus === "needs-gesture" && !expandedView && (
        <div
          onClick={() => wakeLockService.handleUserGesture()}
          className="wake-lock-indicator"
          title="Tap anywhere to keep screen awake"
        >
          <span className="wake-lock-indicator-icon">☀️</span>
          <span>TAP TO WAKE</span>
        </div>
      )}

      {/* Camera Console - shown below island when enabled */}
      {showCameraConsole && expandedView === "camera-controls" && (
        <div
          className="camera-console"
          style={{
            position: "fixed",
            bottom: "0",
            left: "0",
            right: "0",
            maxHeight: "60vh",
            display: "flex",
            flexDirection: "column",
            background: "rgba(0, 0, 0, 0.85)",
            backdropFilter: "blur(20px)",
            borderTop: "1px solid rgba(255, 255, 255, 0.1)",
            zIndex: 1000,
          }}
        >
          {/* Console header */}
          <div
            style={{
              padding: "8px 12px",
              borderBottom: "1px solid rgba(255, 255, 255, 0.1)",
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
            }}
          >
            <div
              style={{
                fontSize: "11px",
                fontWeight: 600,
                color: "var(--text-secondary)",
              }}
            >
              📡 Bluetooth Console
            </div>
            <button
              onClick={() => setCameraLogs([])}
              style={{
                padding: "4px 12px",
                fontSize: "10px",
                background: "rgba(255, 255, 255, 0.1)",
                border: "1px solid rgba(255, 255, 255, 0.2)",
                borderRadius: "4px",
                color: "var(--text-secondary)",
                cursor: "pointer",
              }}
            >
              Clear
            </button>
          </div>

          {/* Console content */}
          <div
            style={{
              flex: 1,
              overflowY: "auto",
              padding: "8px 12px",
              fontSize: "10px",
              fontFamily: "monospace",
              color: "var(--text-secondary)",
              minHeight: "150px",
            }}
          >
            {cameraLogs.length === 0 ? (
              <div style={{ opacity: 0.5 }}>No messages yet...</div>
            ) : (
              cameraLogs.map((log, idx) => (
                <div key={idx} style={{ marginBottom: "2px" }}>
                  {log}
                </div>
              ))
            )}
            <div ref={cameraLogsEndRef} />
          </div>
        </div>
      )}
    </>
  );
};
